
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";
import { calculateBusinessDays, isVacationInCurrentYear } from "@/utils/dateUtils";
import { parseISO } from "date-fns";
import { Tables } from "@/integrations/supabase/types";

interface VacationSummaryProps {
  selectedDate: DateRange | undefined;
  isHalfDay: boolean;
  profile?: Tables<"profiles">;
  approvedVacations?: Tables<"vacation_requests">[];
}

export function VacationSummary({ 
  selectedDate, 
  isHalfDay, 
  profile, 
  approvedVacations = [] 
}: VacationSummaryProps) {
  if (!selectedDate?.from || !selectedDate?.to || !profile?.vacation_days) return null;
  
  const usedVacationDays = approvedVacations
    .filter(vacation => isVacationInCurrentYear(vacation.start_date))
    .reduce((total, vacation) => {
      const start = parseISO(vacation.start_date);
      const end = parseISO(vacation.end_date);
      const days = calculateBusinessDays(start, end);
      return total + (vacation.is_half_day ? days * 0.5 : days);
    }, 0) || 0;
  
  const businessDays = calculateBusinessDays(selectedDate.from, selectedDate.to);
  const requestedDays = isHalfDay ? businessDays * 0.5 : businessDays;
  const remainingDays = profile.vacation_days - usedVacationDays - requestedDays;

  return (
    <div className="relative rounded-lg p-[2px] animate-fade-in overflow-hidden">
      {/* Animated rotating gradient border */}
      <div
        className="absolute inset-0 rounded-lg"
        style={{
          background: "conic-gradient(from var(--border-angle, 0deg), #3b82f6, #8b5cf6, #ec4899, #f59e0b, #10b981, #3b82f6)",
          animation: "rotate-border 3s linear infinite",
        }}
      />
      <div className="relative flex items-center gap-6 flex-wrap rounded-[6px] px-4 py-3 bg-background">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Business Days:</span>
          <span className="font-semibold">{businessDays}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Used Vacation Days:</span>
          <span className="font-semibold">{usedVacationDays}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Requested Days:</span>
          <span className="font-semibold">{requestedDays}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Remaining Days:</span>
          <span className={cn("font-semibold", remainingDays < 0 ? "text-destructive" : "text-green-600")}>
            {remainingDays}
          </span>
        </div>
      </div>
    </div>
  );
}
